// The report builder (demo case 3): source, period, columns, filters; the table searches and sorts
// instantly; export to Excel or CSV; save as a template, shared or not; the standard reports.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sv } from '@aldorock-fordon/shared/sv';
import type {
  ColumnKind,
  ReportDefinition,
  ReportResult,
  ReportSource,
  ReportTemplate,
} from '@aldorock-fordon/shared/reports';
import type { PoolSummary } from '@aldorock-fordon/shared/api-types';
import { api, ApiError, type Me } from '../api.ts';
import { usePage } from '../page.ts';
import { PageHead } from '../ui/PageHead.tsx';
import { CheckboxField, SelectField, TextArea, TextField } from '../ui/Fields.tsx';
import { Table } from '../ui/Table.tsx';
import { kr, moment, num, todayIso } from '../format.ts';

type ColumnInfo = { key: string; kind: ColumnKind; group: string; label: string };
type Unit = { id: string; name: string; kind: string };
type Standard = { key: string; name: string; definition: Omit<ReportDefinition, 'from' | 'to'> };
type Schedule = {
  id: string;
  name: string;
  recipients: string[];
  cadence: string;
  weekday: number | null;
  dayOfMonth: number | null;
  format: string;
  active: boolean;
  lastSentOn: string | null;
  owner: string;
  own: boolean;
  definition: ReportDefinition;
};

const yearStart = () => `${todayIso().slice(0, 4)}-01-01`;
const monthsAgo = (n: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
};

const DEFAULT: ReportDefinition = {
  source: 'fordon',
  columns: ['registrationNumber', 'make', 'model', 'seats', 'mileagePeriodKm'],
  filters: {},
  from: monthsAgo(12),
  to: todayIso(),
  sortBy: 'registrationNumber',
  sortDesc: false,
};

function cell(kind: ColumnKind, v: string | number | null) {
  if (v === null || v === undefined || v === '') return '';
  if (kind === 'money') return kr(Number(v));
  if (kind === 'km') return `${num(Number(v))} km`;
  if (kind === 'number') return typeof v === 'number' ? num(v) : String(v);
  return String(v);
}

export function ReportsPage({ me }: { me: Me }) {
  const h1 = usePage(sv.reports.title);
  const qc = useQueryClient();
  const [d, setD] = useState<ReportDefinition>(DEFAULT);
  const [result, setResult] = useState<ReportResult | null>(null);
  const [quick, setQuick] = useState('');
  const [sort, setSort] = useState<{ key: string; desc: boolean } | null>(null);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [schedName, setSchedName] = useState('');
  const [schedRecipients, setSchedRecipients] = useState(me.user.email ?? '');
  const [schedCadence, setSchedCadence] = useState<'veckovis' | 'manadsvis'>('veckovis');
  const [schedWeekday, setSchedWeekday] = useState('1');
  const [schedDay, setSchedDay] = useState('1');
  const [schedFormat, setSchedFormat] = useState<'xlsx' | 'csv'>('xlsx');
  const [schedMessage, setSchedMessage] = useState<string | null>(null);
  const schedules = useQuery({
    queryKey: ['report-schedules'],
    queryFn: () => api<Schedule[]>('/reports/schedules'),
  });
  const createSchedule = useMutation({
    mutationFn: () =>
      api('/reports/schedules', {
        method: 'POST',
        body: JSON.stringify({
          name: schedName.trim(),
          definition: d,
          recipients: schedRecipients
            .split(/[\n,;]+/)
            .map((r) => r.trim())
            .filter(Boolean),
          cadence: schedCadence,
          weekday: Number(schedWeekday),
          dayOfMonth: Number(schedDay),
          format: schedFormat,
        }),
      }),
    onSuccess: async () => {
      setSchedName('');
      setSchedMessage(sv.schedules.created);
      await qc.invalidateQueries({ queryKey: ['report-schedules'] });
    },
    onError: (err) =>
      setSchedMessage(
        err instanceof ApiError && err.errors[0] ? err.errors[0].message : sv.errors.server,
      ),
  });
  const sendNow = useMutation({
    mutationFn: (id: string) =>
      api<{ queued: number }>(`/reports/schedules/${id}/send`, { method: 'POST' }),
    onSuccess: async (r) => {
      setSchedMessage(sv.schedules.sent(r.queued));
      await qc.invalidateQueries({ queryKey: ['report-schedules'] });
    },
  });
  const removeSchedule = useMutation({
    mutationFn: (id: string) => api(`/reports/schedules/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-schedules'] }),
  });
  const columns = useQuery({
    queryKey: ['report-columns'],
    queryFn: () => api<Record<ReportSource, ColumnInfo[]>>('/reports/columns'),
  });
  const templates = useQuery({
    queryKey: ['report-templates'],
    queryFn: () => api<ReportTemplate[]>('/reports/templates'),
  });
  const standard = useQuery({
    queryKey: ['report-standard'],
    queryFn: () => api<Standard[]>('/reports/standard'),
  });
  const units = useQuery({ queryKey: ['units'], queryFn: () => api<Unit[]>('/vehicles/units') });
  const pools = useQuery({ queryKey: ['pools'], queryFn: () => api<PoolSummary[]>('/pools') });
  const run = useMutation({
    mutationFn: () =>
      api<ReportResult>('/reports/run', { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: (r) => {
      setResult(r);
      setSort(d.sortBy ? { key: d.sortBy, desc: Boolean(d.sortDesc) } : null);
      setError(null);
      setMessage(null);
    },
    onError: (err) =>
      setError(err instanceof ApiError && err.errors[0] ? err.errors[0].message : sv.errors.server),
  });
  const save = useMutation({
    mutationFn: () =>
      api('/reports/templates', {
        method: 'POST',
        body: JSON.stringify({ name: saveName.trim(), shared: saveShared, definition: d }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['report-templates'] });
      setMessage(sv.reports.saved);
      setSaveName('');
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/reports/templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-templates'] }),
  });
  const available = useMemo(() => columns.data?.[d.source] ?? [], [columns.data, d.source]);
  const groups = useMemo(() => {
    const g = new Map<string, ColumnInfo[]>();
    for (const c of available) g.set(c.group, [...(g.get(c.group) ?? []), c]);
    return [...g.entries()];
  }, [available]);
  const rows = useMemo(() => {
    if (!result) return [];
    const q = quick.trim().toLowerCase();
    let out = result.rows;
    if (q)
      out = out.filter((r) =>
        result.columns.some((c) =>
          String(r[c.key] ?? '')
            .toLowerCase()
            .includes(q),
        ),
      );
    if (sort) {
      const dir = sort.desc ? -1 : 1;
      out = [...out].sort((a, b) => {
        const x = a[sort.key];
        const y = b[sort.key];
        if (x === null || x === undefined || x === '') return 1;
        if (y === null || y === undefined || y === '') return -1;
        return (
          (typeof x === 'number' && typeof y === 'number'
            ? x - y
            : String(x).localeCompare(String(y), 'sv')) * dir
        );
      });
    }
    return out;
  }, [result, quick, sort]);
  const toggleColumn = (key: string) =>
    setD({
      ...d,
      columns: d.columns.includes(key) ? d.columns.filter((c) => c !== key) : [...d.columns, key],
    });
  const load = (
    def: Omit<ReportDefinition, 'from' | 'to'> & Partial<Pick<ReportDefinition, 'from' | 'to'>>,
  ) => {
    setD({ ...def, from: def.from ?? d.from, to: def.to ?? d.to, filters: def.filters ?? {} });
    setResult(null);
    setMessage(null);
  };
  const download = async (format: 'xlsx' | 'csv') => {
    const res = await fetch(`/api/v1/reports/export?format=${format}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(d),
      credentials: 'same-origin',
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const name =
      /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ??
      `rapport.${format}`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <PageHead help="reports" title={sv.reports.title} titleRef={h1} />
      <p>{sv.reports.intro}</p>
      <div className="cols cols-2 report-top">
        <section className="block" aria-labelledby="standard">
          <h2 className="block-title" id="standard">
            {sv.reports.standard}
          </h2>
          <ul className="plain link-list">
            {standard.data?.map((s) => (
              <li key={s.key}>
                <button
                  className="link-button"
                  type="button"
                  onClick={() => load(s.definition)}
                  aria-label={sv.reports.load(s.name)}
                >
                  {s.name}
                </button>
              </li>
            ))}
          </ul>
        </section>
        <section className="block" aria-labelledby="templates">
          <h2 className="block-title" id="templates">
            {sv.reports.templates}
          </h2>
          {templates.data && templates.data.length === 0 ? (
            <p className="muted">{sv.table.empty}</p>
          ) : null}
          <ul className="plain">
            {templates.data?.map((t) => (
              <li key={t.id} className="row">
                <button
                  className="link-button"
                  type="button"
                  onClick={() => load(t.definition)}
                  aria-label={sv.reports.load(t.name)}
                >
                  {t.name}
                </button>
                <span className="caption">
                  {t.shared ? `${sv.reports.shared}, ` : ''}
                  {sv.reports.byOwner(t.ownerName)}
                </span>
                {t.own || me.permissions['report.manage'] ? (
                  <button
                    className="btn btn-quiet"
                    type="button"
                    onClick={() => remove.mutate(t.id)}
                    aria-label={sv.reports.remove(t.name)}
                  >
                    {sv.poolAdmin.removeMember}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      </div>

      <Table
        caption={sv.schedules.title}
        isEmpty={(schedules.data?.length ?? 0) === 0}
        empty={sv.schedules.empty}
        hasOptional
      >
        <thead>
          <tr>
            <th scope="col">{sv.schedules.name}</th>
            <th scope="col" className="col-opt">
              {sv.schedules.recipients}
            </th>
            <th scope="col">{sv.schedules.cadence}</th>
            <th scope="col" className="col-opt">
              {sv.schedules.lastSent}
            </th>
            <th scope="col">{sv.columns.action}</th>
          </tr>
        </thead>
        <tbody>
          {schedules.data?.map((s) => (
            <tr key={s.id}>
              <td>
                <button
                  className="link-button"
                  type="button"
                  onClick={() => load(s.definition)}
                  aria-label={sv.reports.load(s.name)}
                >
                  {s.name}
                </button>
              </td>
              <td className="col-opt">{s.recipients.join(', ')}</td>
              <td>
                {sv.schedules.cadences[s.cadence]}
                {s.cadence === 'veckovis'
                  ? `, ${sv.schedules.weekdays[(s.weekday ?? 1) - 1]}`
                  : `, ${sv.schedules.dayOfMonth.toLowerCase()} ${s.dayOfMonth}`}
              </td>
              <td className="col-opt num">{s.lastSentOn ?? sv.schedules.never}</td>
              <td>
                <span className="actions-inline">
                  <button
                    className="btn btn-quiet"
                    type="button"
                    aria-label={sv.schedules.sendNow(s.name)}
                    onClick={() => sendNow.mutate(s.id)}
                  >
                    {sv.schedules.sendNowShort}
                  </button>
                  {s.own || me.permissions['report.manage'] ? (
                    <button
                      className="btn btn-quiet"
                      type="button"
                      aria-label={sv.schedules.remove(s.name)}
                      onClick={() => removeSchedule.mutate(s.id)}
                    >
                      {sv.schedules.removeShort}
                    </button>
                  ) : null}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <form
        className="report-form"
        aria-label={sv.reports.title}
        onSubmit={(e) => {
          e.preventDefault();
          run.mutate();
        }}
      >
        <div className="filter-row">
          <SelectField
            id="source"
            label={sv.reports.source}
            value={d.source}
            options={Object.entries(sv.reports.sources).map(([value, label]) => ({ value, label }))}
            onChange={(v) => {
              const source = v as ReportSource;
              const keep = (columns.data?.[source] ?? []).map((c) => c.key);
              const def =
                source === 'fordon'
                  ? DEFAULT.columns
                  : source === 'service'
                    ? ['doneOn', 'registrationNumber', 'kind', 'description', 'costSek']
                    : [
                        'month',
                        'poolName',
                        'registrationNumber',
                        'bookedHours',
                        'kmDriven',
                        'costPerMilSek',
                      ];
              setD({
                ...d,
                source,
                columns: d.columns.filter((c) => keep.includes(c)).length
                  ? d.columns.filter((c) => keep.includes(c))
                  : def,
                sortBy: null,
              });
              setResult(null);
            }}
          />
          <TextField
            id="from"
            label={sv.reports.from}
            type="date"
            value={d.from}
            onChange={(v) => setD({ ...d, from: v })}
          />
          <TextField
            id="to"
            label={sv.reports.to}
            type="date"
            value={d.to}
            onChange={(v) => setD({ ...d, to: v })}
          />
          <div className="actions-inline period-shortcuts">
            <button
              className="btn btn-quiet"
              type="button"
              onClick={() => setD({ ...d, from: monthsAgo(12), to: todayIso() })}
            >
              {sv.reports.period12}
            </button>
            <button
              className="btn btn-quiet"
              type="button"
              onClick={() => setD({ ...d, from: yearStart(), to: todayIso() })}
            >
              {sv.reports.periodYear}
            </button>
          </div>
        </div>
        <div className="filter-row">
          <SelectField
            id="filterType"
            label={sv.reports.filterType}
            value={d.filters.vehicleType ?? ''}
            placeholder={sv.reports.all}
            options={Object.entries(sv.vehicleTypes).map(([value, label]) => ({ value, label }))}
            onChange={(v) => setD({ ...d, filters: { ...d.filters, vehicleType: v || null } })}
          />
          <SelectField
            id="filterStatus"
            label={sv.reports.filterStatus}
            value={d.filters.status ?? ''}
            placeholder={sv.reports.all}
            options={Object.entries(sv.vehicleStatus).map(([value, label]) => ({ value, label }))}
            onChange={(v) => setD({ ...d, filters: { ...d.filters, status: v || null } })}
          />
          <SelectField
            id="filterUnit"
            label={sv.reports.filterUnit}
            value={d.filters.unitId ?? ''}
            placeholder={sv.reports.all}
            options={(units.data ?? [])
              .filter((u) => u.kind === 'forvaltning')
              .map((u) => ({ value: u.id, label: u.name }))}
            onChange={(v) => setD({ ...d, filters: { ...d.filters, unitId: v || null } })}
          />
          <SelectField
            id="filterPool"
            label={sv.reports.filterPool}
            value={d.filters.poolId ?? ''}
            placeholder={sv.reports.all}
            options={(pools.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
            onChange={(v) => setD({ ...d, filters: { ...d.filters, poolId: v || null } })}
          />
        </div>
        <fieldset className="columns-picker">
          <legend className="label">
            {sv.reports.columns}{' '}
            <span className="muted">({sv.reports.chosen(d.columns.length)})</span>
          </legend>
          <button
            className="btn"
            type="button"
            aria-expanded={pickerOpen}
            aria-controls="column-picker"
            onClick={() => setPickerOpen((o) => !o)}
          >
            {sv.reports.pickColumns}
          </button>
          {pickerOpen ? (
            <div className="cols cols-3" id="column-picker">
              {groups.map(([group, cols]) => (
                <div key={group} className="column-group">
                  <h3 className="subtle-head">{sv.reports.groups[group] ?? group}</h3>
                  {cols.map((c) => (
                    <label className="choice" htmlFor={`col-${c.key}`} key={c.key}>
                      <input
                        type="checkbox"
                        id={`col-${c.key}`}
                        checked={d.columns.includes(c.key)}
                        onChange={() => toggleColumn(c.key)}
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          ) : null}
          {!pickerOpen ? (
            <p className="caption">
              {d.columns.map((k) => available.find((c) => c.key === k)?.label ?? k).join(', ')}
            </p>
          ) : null}
        </fieldset>
        {error ? (
          <p className="notice" data-state="bad" role="alert">
            {error}
          </p>
        ) : null}
        <div className="form-actions">
          <button
            className="btn btn-primary"
            type="submit"
            disabled={run.isPending || d.columns.length === 0}
            aria-busy={run.isPending}
          >
            {sv.reports.run}
          </button>
        </div>
      </form>

      {result ? (
        <section className="block" aria-labelledby="result">
          <h2 className="block-title" id="result">
            {sv.reports.result}
          </h2>
          <p className="caption">
            {sv.reports.generated(moment(result.generatedAt))}.{' '}
            {sv.reports.scope(result.scopeLabel)}.
          </p>
          <div className="filter-row">
            <TextField
              id="quick"
              label={sv.reports.quickFilter}
              help={sv.reports.quickFilterHelp}
              value={quick}
              onChange={setQuick}
            />
            <div className="actions-inline">
              <button className="btn" type="button" onClick={() => download('xlsx')}>
                {sv.reports.exportXlsx}
              </button>
              <button className="btn" type="button" onClick={() => download('csv')}>
                {sv.reports.exportCsv}
              </button>
            </div>
          </div>
          <p role="status" className="muted">
            {rows.length === result.rows.length
              ? sv.reports.rows(rows.length)
              : sv.reports.rowsOf(rows.length, result.rows.length)}
          </p>
          {result.rows.length === 0 ? <p className="muted">{sv.reports.empty}</p> : null}
          <div
            className="table-wrap"
            tabIndex={0}
            role="region"
            aria-label={sv.table.scrollLabel(sv.reports.result)}
          >
            <table className="table report-table">
              <caption className="visually-hidden">{sv.reports.result}</caption>
              <thead>
                <tr>
                  {result.columns.map((c) => (
                    <th
                      scope="col"
                      key={c.key}
                      className={c.kind === 'text' || c.kind === 'date' ? undefined : 'r'}
                      aria-sort={
                        sort?.key === c.key ? (sort.desc ? 'descending' : 'ascending') : 'none'
                      }
                    >
                      <button
                        className="sort-button"
                        type="button"
                        onClick={() =>
                          setSort({ key: c.key, desc: sort?.key === c.key ? !sort.desc : false })
                        }
                        aria-label={`${sv.reports.sortBy(c.label)}${sort?.key === c.key ? `, ${sort.desc ? sv.reports.sortedDesc : sv.reports.sortedAsc}` : ''}`}
                      >
                        {c.label}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 500).map((r) => (
                  <tr key={String(r.id)}>
                    {result.columns.map((c) => (
                      <td
                        key={c.key}
                        className={
                          c.kind === 'text' || c.kind === 'date'
                            ? c.key.startsWith('fritext:')
                              ? 'free-text'
                              : undefined
                            : 'r'
                        }
                      >
                        {cell(c.kind, r[c.key] ?? null)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <form
            className="form-block save-template"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <h3 className="form-heading">{sv.reports.save}</h3>
            <TextField
              id="saveName"
              label={sv.reports.saveName}
              value={saveName}
              onChange={setSaveName}
            />
            {me.permissions['report.manage'] ? (
              <CheckboxField
                id="saveShared"
                label={sv.reports.saveShared}
                checked={saveShared}
                onChange={setSaveShared}
              />
            ) : null}
            {message ? (
              <p className="notice" data-state="done" role="status">
                {message}
              </p>
            ) : null}
            <div className="form-actions">
              <button className="btn" type="submit" disabled={save.isPending || !saveName.trim()}>
                {sv.reports.saveButton}
              </button>
            </div>
          </form>
          <form
            className="form-block schedule-form"
            aria-labelledby="schedule"
            onSubmit={(e) => {
              e.preventDefault();
              createSchedule.mutate();
            }}
          >
            <h3 className="form-heading" id="schedule">
              {sv.schedules.title}
            </h3>
            <p className="caption">{sv.schedules.intro}</p>
            <TextField
              id="schedName"
              label={sv.schedules.name}
              value={schedName}
              onChange={setSchedName}
            />
            <TextArea
              id="schedRecipients"
              label={sv.schedules.recipients}
              value={schedRecipients}
              rows={2}
              onChange={setSchedRecipients}
            />
            <SelectField
              id="schedCadence"
              label={sv.schedules.cadence}
              value={schedCadence}
              options={Object.entries(sv.schedules.cadences).map(([value, label]) => ({
                value,
                label,
              }))}
              onChange={(v) => setSchedCadence(v as 'veckovis' | 'manadsvis')}
            />
            {schedCadence === 'veckovis' ? (
              <SelectField
                id="schedWeekday"
                label={sv.schedules.weekday}
                value={schedWeekday}
                options={sv.schedules.weekdays.map((w, i) => ({ value: String(i + 1), label: w }))}
                onChange={setSchedWeekday}
              />
            ) : (
              <TextField
                id="schedDay"
                label={sv.schedules.dayOfMonth}
                type="number"
                value={schedDay}
                onChange={setSchedDay}
              />
            )}
            <SelectField
              id="schedFormat"
              label={sv.schedules.format}
              value={schedFormat}
              options={Object.entries(sv.schedules.formats).map(([value, label]) => ({
                value,
                label,
              }))}
              onChange={(v) => setSchedFormat(v as 'xlsx' | 'csv')}
            />
            {schedMessage ? (
              <p className="notice" data-state="done" role="status">
                {schedMessage}
              </p>
            ) : null}
            <div className="form-actions">
              <button
                className="btn"
                type="submit"
                disabled={createSchedule.isPending || !schedName.trim() || !schedRecipients.trim()}
              >
                {sv.schedules.create}
              </button>
            </div>
          </form>
        </section>
      ) : null}
    </>
  );
}
